import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Header,
  RadioGroup,
  SpaceBetween,
  Spinner,
  Textarea,
} from '@amzn/awsui-components-react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  FIELD_1_LABEL,
  FIELD_2_HELPER_TEXT,
  FIELD_2_HELPER_TEXT_POSITIVE,
  FIELD_2_LABEL,
  FIELD_2_LABEL_POSITIVE,
  FIELD_3_LABEL,
  FIELD_3_REQUIRED_MESSAGE,
  FIELD_4_LABEL,
  FIELD_4_REQUIRED_MESSAGE,
  FORM_HEADER,
  FORM_TITLE,
  FREE_TEXT_MAX_LENGTH,
  FREE_TEXT_STORAGE_NOTICE,
  RATING_LABELS,
  RATING_OPTIONS,
  WRONG_PART_TAG_OPTIONS,
} from './constants';
import { Rating, WrongPartTag } from './types';

/**
 * The one Feedback_Form, shared by the chat modal and the standalone ticket page.
 *
 * Both ticket branches render *this* component; `r` supplies the pre-set Rating and nothing else. There
 * is no second surface and no confirmation control.
 */

/** What the host does with a completed draft. Resolves to the record identifier, or rejects. */
export type SubmitHandler = (draft: SubmittedDraft) => Promise<string>;

export interface SubmittedDraft {
  rating: Rating;
  whatShouldItHaveSaid: string;
  /** Omitted entirely when field 3 was hidden at submit time. */
  whatDidWeGetWrong?: string;
  wrongPartTags: WrongPartTag[];
}

export interface FeedbackFormProps {
  /** Rendered between the title and field 1. The ticket page passes its context subtitle here. */
  contextSubtitle?: React.ReactNode;
  /** Pre-set Rating. This is the only way the two ticket branches differ. */
  initialRating?: Rating | null;
  /** The only per-open value the host passes in. Diagnostic; never part of a target reference. */
  formOpenId: string;
  onSubmit: SubmitHandler;
  /** Takes no argument: no dismissal reason is captured, plumbed, or persisted anywhere. */
  onSkip: () => void;
}

type Outcome =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }
  | { kind: 'unknown' };

/*
 * The four predicates below now share one condition, `rating === 'Down'`, and are deliberately still four.
 *
 * They were distinct when a middle `Partial` rating existed: it showed the diagnostic fields while leaving
 * them optional, so "visible" and "required" genuinely disagreed. Dropping it made the conditions coincide.
 * Collapsing them into a single `isNegative` would erase which question each field answers, and would make
 * the next divergence — a field that should appear on `Up`, or a diagnostic that becomes optional again —
 * look like a bug in whichever field was reading the shared predicate.
 */

/** Field 3 is visible exactly when the Rating is `Down`. */
export function isField3Visible(rating: Rating | null): boolean {
  return rating === 'Down';
}

/**
 * Field 2 is hidden for `Down`, where field 3 asks the same thing more precisely.
 *
 * "What should it have said or done?" and "What did we get wrong?" are two free-text boxes about the same
 * failure. Showing both on a negative rating invites the answer to be split across them, or typed once and
 * the other left blank — and then a reader has to check two places. On `Down` the diagnostic pair is
 * required, so the general box earns nothing.
 *
 * Written as `!== 'Down'` rather than `=== 'Up'`: with no rating selected the field should still show, so
 * the absence of a negative is the condition, not the presence of a positive.
 */
export function isField2Visible(rating: Rating | null): boolean {
  return rating !== 'Down';
}

/** `Down` requires both diagnostic fields. It is the only rating that requires anything. */
export function requiresDiagnostics(rating: Rating | null): boolean {
  return rating === 'Down';
}

/**
 * Field 4 follows field 3: "which part was wrong" only has an answer when something was wrong.
 *
 * Separate from {@link isField3Visible} despite the identical condition, for the reason given above.
 */
export function isField4Visible(rating: Rating | null): boolean {
  return rating === 'Down';
}

/**
 * Which ratings the form offers, derived from the rating the surface arrived with.
 *
 * The link or thumb the user pressed *is* the rating; field 1 exists to show it, not to re-ask. So the
 * offered set is narrowed to where the user started: flipping a rating to its opposite would file a
 * complaint through the approval path or vice versa, and the two separate controls exist precisely so that
 * cannot happen by misclick. With no preset, nothing has been asserted yet and both are offered.
 *
 * Derived from `initialRating` rather than the live selection, so the option set is stable while the user is
 * looking at it — recomputing from the current rating would let a change shrink the very list being clicked.
 */
export function allowedRatings(
  initialRating: Rating | null
): readonly Rating[] {
  return initialRating === null ? RATING_OPTIONS : [initialRating];
}

/**
 * A stable handle for a tag's checkbox, derived from the label but not equal to it.
 *
 * Exported so tests address tags the same way the component labels them, rather than hard-coding slugs that
 * would silently stop matching if this derivation changed.
 */
export function tagSlug(tag: WrongPartTag): string {
  return tag.toLowerCase().replace(/\s+/g, '-');
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  contextSubtitle,
  initialRating = null,
  formOpenId,
  onSubmit,
  onSkip,
}) => {
  const [rating, setRating] = useState<Rating | null>(initialRating);
  const [whatShouldItHaveSaid, setWhatShouldItHaveSaid] = useState('');
  // Held independently of visibility so it survives a hide and restores exactly on re-show. Losing it
  // would silently discard something the user typed.
  const [whatDidWeGetWrong, setWhatDidWeGetWrong] = useState('');
  const [tags, setTags] = useState<WrongPartTag[]>([]);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [field3Error, setField3Error] = useState<string | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });

  const inFlight = outcome.kind === 'submitting';
  const succeeded = outcome.kind === 'success';

  // Recomputed on every render from the current Rating, with no memo of prior visibility, so the
  // equivalence with the predicate holds structurally rather than by careful bookkeeping.
  const field2Visible = isField2Visible(rating);
  const field3Visible = isField3Visible(rating);
  const field4Visible = isField4Visible(rating);
  const diagnosticsRequired = requiresDiagnostics(rating);
  // A positive rating leaves field 2 as the only input, so it asks for a comment rather than a correction.
  const positive = rating === 'Up';

  // Submit is disabled without a Rating, while a submission is in flight, and after a success until the
  // host closes the form.
  const submitDisabled = rating === null || inFlight || succeeded;
  // Skip stays enabled whenever nothing is in flight.
  /**
   * Includes `succeeded`, like `submitDisabled` and `readOnly`.
   *
   * The Submit/Skip row stays rendered under the success alert, so a Skip gated only on `inFlight` remained
   * clickable after the write had landed. On the ticket page that is actively misleading rather than merely
   * inert: `onSkip` sets `dismissed`, which replaces the page with "No feedback was recorded" — telling the
   * user nothing was stored moments after showing them that it was.
   */
  const skipDisabled = inFlight || succeeded;

  const onFreeTextChange = useCallback((value: string) => {
    // Truncate on change rather than rejecting the keystroke, so a 5000-character paste keeps its first
    // 2000 instead of being discarded whole.
    setWhatShouldItHaveSaid(value.slice(0, FREE_TEXT_MAX_LENGTH));
  }, []);

  const onField3Change = useCallback((value: string) => {
    setWhatDidWeGetWrong(value.slice(0, FREE_TEXT_MAX_LENGTH));
    if (value.trim() !== '') setField3Error(null);
  }, []);

  const toggleTag = useCallback((tag: WrongPartTag, checked: boolean) => {
    if (checked) setTagsError(null);
    setTags((current) =>
      checked
        ? current.includes(tag)
          ? current
          : [...current, tag]
        : current.filter((t) => t !== tag)
    );
  }, []);

  const atCap = whatShouldItHaveSaid.length >= FREE_TEXT_MAX_LENGTH;
  /**
   * Field 3 caps identically to field 2, so it says so identically.
   *
   * `onField3Change` already truncates at the cap. Without the indication the truncation is silent, and it
   * is silent on the one field a `Down` rating requires — so the field most likely to be pasted into at
   * length is the one that loses the overflow without saying it did.
   */
  const field3AtCap = whatDidWeGetWrong.length >= FREE_TEXT_MAX_LENGTH;
  /**
   * Inputs are frozen once the write has landed, not only while it is in flight.
   *
   * `submitDisabled` already includes `succeeded`, but the inputs were gated on `inFlight` alone — and after
   * a success `inFlight` is false while `succeeded` is true. That left every field editable with no way to
   * resubmit, so the form could be made to show edited values above a notice saying the feedback was
   * recorded, describing something that was never sent.
   */
  const readOnly = inFlight || succeeded;

  const handleSubmit = useCallback(async () => {
    /*
     * Both early returns below clear `outcome` first.
     *
     * A validation failure sends nothing, so any alert from a *previous* attempt is describing a request that
     * is no longer the current state of this form. Without this, a failed submission followed by a click that
     * fails validation rendered the old "something went wrong" alert above the new field errors — reporting a
     * transport failure that did not happen this time, next to the reason nothing was sent.
     *
     * Only reachable while the form is still editable: after a success `submitDisabled` and `readOnly` both
     * hold, so this handler cannot be re-entered to un-freeze it.
     */
    if (rating === null) {
      // No request, and nothing the user typed is disturbed.
      setOutcome({ kind: 'idle' });
      setRatingError('Select an option before submitting.');
      const el = document.getElementById(`merlin-field-1-${formOpenId}`);
      if (el) el.focus();
      return;
    }
    setRatingError(null);

    /*
     * `No` requires both diagnostics. Checked here rather than by disabling Submit: a disabled button
     * states that something is missing without saying what, so the user is left hunting. Both are
     * validated in one pass so a single click reports everything outstanding, instead of surfacing the
     * second problem only after the first is fixed.
     */
    if (requiresDiagnostics(rating)) {
      const missingField3 = whatDidWeGetWrong.trim() === '';
      const missingTags = tags.length === 0;
      setField3Error(missingField3 ? FIELD_3_REQUIRED_MESSAGE : null);
      setTagsError(missingTags ? FIELD_4_REQUIRED_MESSAGE : null);
      if (missingField3 || missingTags) {
        // Nothing is sent, and nothing the user typed is disturbed. See the note on the return above for
        // why the previous attempt's outcome is cleared rather than left on screen.
        setOutcome({ kind: 'idle' });
        return;
      }
    } else {
      setField3Error(null);
      setTagsError(null);
    }

    // Everything is disabled in this handler, before the request is issued — not in an effect reacting
    // to a flag, which would run after a render that briefly exposed an enabled control.
    setOutcome({ kind: 'submitting' });

    try {
      const recordId = await onSubmit({
        rating,
        // Dropped when field 2 is hidden, on the same principle as fields 3 and 4: text typed before the
        // rating became `No` must not ride along beside the diagnostics that replaced it.
        whatShouldItHaveSaid: isField2Visible(rating)
          ? whatShouldItHaveSaid.trim()
          : '',
        // Omitted whenever field 3 is hidden, even though the text is still held in state.
        ...(isField3Visible(rating) && whatDidWeGetWrong.trim()
          ? { whatDidWeGetWrong: whatDidWeGetWrong.trim() }
          : {}),
        // Dropped whenever field 4 is hidden, on the same principle as field 3: tags ticked before the
        // rating was changed to Yes must not ride along and describe an answer as faulty that the user
        // then endorsed.
        wrongPartTags: isField4Visible(rating) ? tags : [],
      });
      if (!recordId) {
        // A response with no identifier is an unknown outcome, not a success: the write may or may not
        // have landed, so the message says retrying is safe rather than claiming either.
        setOutcome({ kind: 'unknown' });
        return;
      }
      setOutcome({ kind: 'success' });
    } catch (e: any) {
      /*
       * `||`, not `??`. `??` substitutes only for `null`/`undefined`, so a rejection carrying an empty
       * message — `new Error('')`, or a thrown value whose `message` is `''` — passed straight through and
       * rendered an error Alert with nothing in it. An empty alert is worse than a generic one: it tells the
       * user something failed while withholding what, and looks like a rendering bug rather than a report.
       */
      const message: string = e?.message || 'Something went wrong.';
      // A timeout is indistinguishable from a slow success from here, so it is reported as unknown.
      setOutcome(
        message.toLowerCase().includes('timeout')
          ? { kind: 'unknown' }
          : { kind: 'error', message }
      );
    }
  }, [
    formOpenId,
    onSubmit,
    rating,
    tags,
    whatDidWeGetWrong,
    whatShouldItHaveSaid,
  ]);

  // Labelled through RATING_LABELS, never with the raw value: `Up`/`Down` exist to match what Insight
  // writes to the shared store, and they are not an answer to "Was this useful?".
  /*
   * Disabled per item, because awsui's `RadioGroup` takes no group-level `disabled` — the flag lives on each
   * `RadioButtonDefinition`. Worth stating: the absence of a `disabled` prop on the group reads like an
   * oversight in the markup, and putting one there compiles as an excess-property error rather than being
   * silently ignored.
   */
  const ratingItems = useMemo(
    () =>
      allowedRatings(initialRating).map((value) => ({
        value,
        label: RATING_LABELS[value],
        disabled: readOnly,
      })),
    [initialRating, readOnly]
  );

  return (
    <div data-testid="merlin-feedback-form">
      <SpaceBetween size="m">
        <Header variant="h2" description={FORM_TITLE}>
          {FORM_HEADER}
        </Header>

        {contextSubtitle ? <Box variant="small">{contextSubtitle}</Box> : null}

        {/* Field 1 */}
        <FormField
          label={<strong>{FIELD_1_LABEL}</strong>}
          errorText={ratingError ?? undefined}
        >
          <RadioGroup
            // Programmatic label plus a stable id, so the error can move focus here.
            ariaLabel={FIELD_1_LABEL}
            items={ratingItems}
            value={rating}
            onChange={({ detail }) => {
              setRating(detail.value as Rating);
              /*
               * All three errors, not just this field's.
               *
               * The rating decides which fields exist and which are required, so a diagnostic error raised
               * under the previous rating describes a question that is no longer being asked. Clearing only
               * `ratingError` left the other two set but hidden while on `Up`, and switching back to `Down`
               * re-displayed them against fields the user had not touched since — an error that had already
               * been answered, or never applied.
               *
               * Re-raising them is Submit's job: `handleSubmit` recomputes all three from the current rating,
               * so nothing is lost by clearing them here.
               */
              setRatingError(null);
              setField3Error(null);
              setTagsError(null);
              /*
               * The submission outcome too, for the same reason the early returns in `handleSubmit` clear it:
               * an alert from a previous attempt describes a request that is no longer this form's state.
               *
               * Reachable because neither `error` nor `unknown` sets `readOnly` — that is `inFlight ||
               * succeeded` — so the rating stays editable with a failure alert on screen. Changing the rating
               * then invalidates that attempt more thoroughly than a validation failure does, since it can
               * hide the very fields the attempt was about.
               */
              setOutcome({ kind: 'idle' });
            }}
            data-testid="merlin-field-1"
          />
          <span id={`merlin-field-1-${formOpenId}`} tabIndex={-1} />
        </FormField>

        {/* Field 2 — hidden for `No`, where field 3 asks the same thing more precisely. */}
        {field2Visible ? (
          <FormField
            label={
              <strong>
                {positive ? FIELD_2_LABEL_POSITIVE : FIELD_2_LABEL}
              </strong>
            }
            description={
              positive ? FIELD_2_HELPER_TEXT_POSITIVE : FIELD_2_HELPER_TEXT
            }
            constraintText={
              atCap
                ? `Maximum ${FREE_TEXT_MAX_LENGTH} characters reached.`
                : undefined
            }
          >
            <Textarea
              ariaLabel={positive ? FIELD_2_LABEL_POSITIVE : FIELD_2_LABEL}
              value={whatShouldItHaveSaid}
              disabled={readOnly}
              onChange={({ detail }) => onFreeTextChange(detail.value)}
              data-testid="merlin-field-2"
            />
          </FormField>
        ) : null}

        {/* Field 3 — visible only for Partial or No. */}
        {field3Visible ? (
          <FormField
            label={<strong>{FIELD_3_LABEL}</strong>}
            // Marker in the description, not concatenated into the label: the label stays a single exact
            // string for assistive tech and for anything matching on it.
            description={diagnosticsRequired ? 'Required.' : undefined}
            constraintText={
              field3AtCap
                ? `Maximum ${FREE_TEXT_MAX_LENGTH} characters reached.`
                : undefined
            }
            errorText={field3Error ?? undefined}
          >
            <Textarea
              ariaLabel={FIELD_3_LABEL}
              value={whatDidWeGetWrong}
              disabled={readOnly}
              onChange={({ detail }) => onField3Change(detail.value)}
              data-testid="merlin-field-3"
            />
          </FormField>
        ) : null}

        {/* Field 4 — visible only for Partial or No, like field 3. */}
        {field4Visible ? (
          <FormField
            label={<strong>{FIELD_4_LABEL}</strong>}
            // Marker in the description, not concatenated into the label: the label stays a single exact
            // string for assistive tech and for anything matching on it.
            description={diagnosticsRequired ? 'Required.' : undefined}
            errorText={tagsError ?? undefined}
          >
            <SpaceBetween size="xs">
              {WRONG_PART_TAG_OPTIONS.map((tag) => (
                <Checkbox
                  key={tag}
                  checked={tags.includes(tag)}
                  disabled={readOnly}
                  onChange={({ detail }) => toggleTag(tag, detail.checked)}
                  // Slugged, not the label itself. Interpolating the label put spaces in the id and made
                  // every test that selects a tag depend on its wording — so a copy change broke tests that
                  // had nothing to say about copy.
                  data-testid={`merlin-tag-${tagSlug(tag)}`}
                >
                  {tag}
                </Checkbox>
              ))}
            </SpaceBetween>
          </FormField>
        ) : null}

        <Box variant="small">{FREE_TEXT_STORAGE_NOTICE}</Box>

        {/* Submit precedes Skip in both visual and focus order. */}
        <SpaceBetween size="xs" direction="horizontal">
          <Button
            variant="primary"
            disabled={submitDisabled}
            onClick={handleSubmit}
            data-testid="merlin-submit"
          >
            Submit
          </Button>
          {/*
            Wrapped rather than passed directly: `onClick={onSkip}` would hand `onSkip` the React
            click event, so "takes no argument" would hold in the type and not at runtime. That gap is
            worth closing — an event object sitting in `arguments[0]` is the sort of thing a later
            change reads as "a dismissal reason is available here", which is the plumbing this feature
            deliberately does not have.
          */}
          <Button
            disabled={skipDisabled}
            onClick={() => onSkip()}
            data-testid="merlin-skip"
          >
            Skip
          </Button>
        </SpaceBetween>

        {inFlight ? (
          <Box data-testid="merlin-loading">
            <Spinner /> Submitting…
          </Box>
        ) : null}
        {outcome.kind === 'success' ? (
          <Alert type="success" data-testid="merlin-success">
            Thanks — your feedback was recorded.
          </Alert>
        ) : null}
        {outcome.kind === 'error' ? (
          <Alert type="error" data-testid="merlin-error">
            {outcome.message}
          </Alert>
        ) : null}
        {outcome.kind === 'unknown' ? (
          <Alert type="warning" data-testid="merlin-unknown">
            We could not confirm whether your feedback was recorded. Retrying is
            safe — a repeat submission replaces the previous one rather than
            adding a second.
          </Alert>
        ) : null}
      </SpaceBetween>
    </div>
  );
};
